import React, { useCallback } from "react";
import { Stack, Button, styled, Popper, Card, OutlinedInput, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import { useSelection } from "../../../Utils/Hooks/useSelection";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { DbUserGroupRoleInfos } from "../../../Core/Api/Types/db-user-group-role-infos";
import { useTranslation } from "react-i18next";

export interface CopyPermissionRolePopperProps {
  anchorEl: HTMLButtonElement | undefined;
  allRoles: DbUserGroupRoleInfos[];
  onClickConfirm: (acls: AclTypes[] | null | undefined) => void;
  onClose: () => void;
}

const CopyPermissionRolePopper: React.FC<CopyPermissionRolePopperProps> = (props) => {
  const { t } = useTranslation();
  const { anchorEl, allRoles, onClickConfirm, onClose } = props;
  const { select, isSelected, selected } = useSelection();
  const [filter, setFilter] = React.useState("");

  const confirm = useCallback(() => {
    onClickConfirm(allRoles.find((group) => group.name === selected)?.acls ?? []);
  }, [allRoles, onClickConfirm, selected]);

  return (
    <StyledPopper
      id="copy-permissions"
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      sx={(theme) => ({ zIndex: 1301 })}
      placement="bottom-end"
    >
      <StyledCard>
        <StyledHeader>
          <OutlinedInput
            label={t("common:search")}
            size="small"
            sx={{ width: "100%" }}
            placeholder={t("common:search")}
            onChange={(e) => setFilter(e.target.value)}
            endAdornment={
              <InputAdornment position="end">
                <SearchIcon />
              </InputAdornment>
            }
          />
        </StyledHeader>
        <ul style={{ padding: 0, margin: 0 }}>
          {allRoles
            .filter((x) => filter === "" || x.name?.includesAllWords(filter))
            .map((group) => (
              <StyledListItem key={group.name} onClick={() => select(group.name!)} selected={isSelected(group.name!)}>
                {group.name} <CheckIcon color="primary" fontSize="small" />
              </StyledListItem>
            ))}
        </ul>
        <StyledFooter>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" color="primary" size="small" onClick={confirm} fullWidth disabled={!selected}>
              {t("common:copy")}
            </Button>
            <Button variant="contained" color="inherit" size="small" onClick={onClose}>
              {t("common:close")}
            </Button>
          </Stack>
        </StyledFooter>
      </StyledCard>
    </StyledPopper>
  );
};

export default CopyPermissionRolePopper;

const StyledPopper = styled(Popper)(({ theme }) => ({
  zIndex: 1301,
}));

const StyledCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1),
  border: `1px solid ${theme.palette.backgroundAlpha.border}`,
}));

const StyledListItem = styled("li", { shouldForwardProp: (prop) => prop !== "selected" })<{ selected?: boolean }>(
  ({ theme, selected }) => ({
    display: "flex",
    justifyContent: "space-between",
    padding: "6px 10px",
    borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
    cursor: "pointer",
    transition: "background .3s",
    ".MuiSvgIcon-root": {
      transition: "opacity .3s",
      opacity: selected ? 1 : 0,
    },
    "&:hover": {
      background: theme.palette.backgroundAlpha.hover,
    },
  })
);

const StyledHeader = styled(Stack)(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: theme.palette.background.paper,
  position: "sticky",
  top: 0,
  padding: theme.spacing(1),
}));

const StyledFooter = styled(Stack)(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.backgroundAlpha.border}`,
  background: theme.palette.background.paper,
  position: "sticky",
  bottom: 0,
  padding: theme.spacing(1),
}));
